import { ChevronDownIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useUserProviders } from "../../../../hooks/useUserProviders";
import { useClientTranslations } from "../../../../lib/i18n/client";
import styles from "./ContactPicker.module.css";
import { FormField } from "./FormField";

interface ContactPickerProps {
	id: string;
	label: string;
	required?: boolean;
	error?: string;
	placeholder?: string;
	value: string | null; // selected providerUserId (UUID) | null
	onSelect: (providerUserId: string) => void;
}

/**
 * Phase 37 — Inline dropdown over useUserProviders.
 *
 * D-14 / C-09 / ADD-09: NO free-text contact entry. Provider list is the only
 * source. Empty state inline (Pitfall 14) — Save stays disabled because
 * contactId remains null. `value` is the providerUserId (UUID) so the parent
 * form can wire it directly to Event.contactId on submit.
 */
export function ContactPicker({
	id,
	label,
	required,
	error,
	placeholder,
	value,
	onSelect,
}: ContactPickerProps) {
	const { t } = useClientTranslations("track");
	const { data, isLoading, isError } = useUserProviders();
	const [open, setOpen] = useState(false);

	const providers = data?.providers ?? [];
	const selected = providers.find((p) => p.providerUserId === value) ?? null;
	const displayValue = selected
		? `${selected.providerName} — ${selected.businessName}`
		: (placeholder ?? t("event.contact.placeholder"));

	return (
		<FormField id={id} label={label} required={required} error={error}>
			<button
				id={id}
				type="button"
				className={`${styles.trigger} ${selected ? "" : styles.triggerEmpty}`}
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				aria-haspopup="listbox"
				aria-describedby={error ? `${id}-error` : undefined}
			>
				<UserCircleIcon className={styles.triggerIcon} aria-hidden="true" />
				<span className={styles.triggerLabel}>{displayValue}</span>
				<ChevronDownIcon
					className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
					aria-hidden="true"
				/>
			</button>
			{open ? (
				<div className={styles.popover} role="listbox" aria-label={label}>
					{isLoading ? (
						<p className={styles.message}>{t("event.contact.loading")}</p>
					) : isError ? (
						<p className={styles.messageError}>
							{t("event.add.errors.network")}
						</p>
					) : providers.length === 0 ? (
						<div className={styles.empty}>
							<p className={styles.emptyTitle}>
								{t("event.contact.empty.title")}
							</p>
							<p className={styles.emptyBody}>
								{t("event.contact.empty.body")}
							</p>
						</div>
					) : (
						providers.map((p) => (
							<button
								key={p.providerUserId}
								type="button"
								role="option"
								aria-selected={p.providerUserId === value}
								className={`${styles.row} ${
									p.providerUserId === value ? styles.rowSelected : ""
								}`}
								onClick={() => {
									onSelect(p.providerUserId);
									setOpen(false);
								}}
							>
								<span className={styles.rowName}>{p.providerName}</span>
								<span className={styles.rowBusiness}>{p.businessName}</span>
							</button>
						))
					)}
				</div>
			) : null}
		</FormField>
	);
}
